// Resolve a repository to a launchable working directory on an AHP host: match
// the host's projects catalogue or clone it and wait for the clone to settle.
// The resolved `path` is what a worktree `createSession` binds to. Opt-in: only
// the repo-scoped AHP launch path uses it, after `initialize`, before `createSession`.

import type {AhpClient, Subscription} from '@microsoft/agent-host-protocol/client'
import type {RootConfigChangedAction, RootState, StateAction} from '@microsoft/agent-host-protocol'
import {
  matchProjectByCloneUrl,
  readProjectManagementAvailable,
  readProjectsCatalogue,
  readProjectsFromConfigDelta,
  type ProjectEntry,
} from './project-catalogue'
import {cloneProject} from './project-commands'

const ACTION_ROOT_CONFIG_CHANGED = 'root/configChanged'
export const PROJECT_PROVISIONING_TIMEOUT_MS = 180_000

export type ProvisioningPhase = 'preparing' | 'cloning' | 'ready' | 'failed'

/** A provisioning progress update; `progress` is a 0..100 clone percent. */
export interface ProvisioningStatus {
  phase: ProvisioningPhase
  progress?: number
  error?: string
}

export type ProvisioningFailureReason = 'unsupported' | 'clone-failed' | 'timeout' | 'closed'

/** Raised when a repo cannot be resolved to a launchable project. */
export class ProjectProvisioningError extends Error {
  readonly reason: ProvisioningFailureReason
  constructor(reason: ProvisioningFailureReason, message: string) {
    super(message)
    this.name = 'ProjectProvisioningError'
    this.reason = reason
  }
}

export interface ResolveProjectOptions {
  client: AhpClient
  rootState: RootState | undefined
  /** Clone URL used to fetch the repo; also the host-aware catalogue match key. */
  cloneUrl: string
  /** Branch to check out when cloning a project that is not already catalogued. */
  cloneBranch?: string
  /** Attaches a subscription to the already-subscribed root channel. */
  watchRoot: () => Subscription
  /** Ensures the clone credential is authenticated before a private clone. */
  authenticate?: () => Promise<void>
  onProgress?: (status: ProvisioningStatus) => void
  timeoutMs?: number
  signal?: AbortSignal
}

export interface ResolvedProject {
  path: string
  defaultBranch?: string
}

function isReady(entry: ProjectEntry): boolean {
  return entry.status === 'ready' && !!entry.path
}

function toResolved(entry: ProjectEntry): ResolvedProject {
  return {path: entry.path, ...(entry.defaultBranch ? {defaultBranch: entry.defaultBranch} : {})}
}

function isRootConfigChanged(action: StateAction): action is RootConfigChangedAction {
  return action.type === ACTION_ROOT_CONFIG_CHANGED
}

/**
 * Resolve a repository to a launchable working directory. Throws
 * `ProjectProvisioningError('unsupported')` when the host lacks project
 * management; returns immediately for an already-`ready` project; otherwise
 * authenticates, clones, and watches `root/configChanged` until the entry
 * settles to `ready` (resolve) or `failed` (throw).
 */
export async function resolveProjectWorkingDirectory({
  client,
  rootState,
  cloneUrl,
  cloneBranch,
  watchRoot,
  authenticate,
  onProgress,
  timeoutMs = PROJECT_PROVISIONING_TIMEOUT_MS,
  signal,
}: ResolveProjectOptions): Promise<ResolvedProject> {
  signal?.throwIfAborted()
  if (!readProjectManagementAvailable(rootState)) {
    throw new ProjectProvisioningError(
      'unsupported',
      'This environment does not support repository projects (dynamic project management is disabled).',
    )
  }

  const existing = matchProjectByCloneUrl(readProjectsCatalogue(rootState), cloneUrl)
  // Authentication belongs to connection setup, not the clone's absolute budget.
  if (!existing) {
    await authenticate?.()
    signal?.throwIfAborted()
  }
  onProgress?.({phase: 'preparing'})
  if (existing) {
    if (existing.status === 'failed') {
      // Surface a prior terminal `failed` entry immediately rather than silently
      // re-cloning, which could mask a persistently failing repo.
      onProgress?.({phase: 'failed', error: existing.error})
      throw new ProjectProvisioningError(
        'clone-failed',
        existing.error ?? 'A previous clone of this repository failed on the host.',
      )
    }
    if (isReady(existing)) {
      onProgress?.({phase: 'ready'})
      return toResolved(existing)
    }
  }

  // Attach the root watcher before cloning so no clone-advancing delta is missed.
  const subscription = watchRoot()
  const expiresAt = performance.now() + timeoutMs
  const timeoutError = () =>
    new ProjectProvisioningError('timeout', `The repository clone did not finish within ${timeoutMs}ms.`)
  let timer: ReturnType<typeof setTimeout> | undefined
  let onAbort = () => {}
  const interrupted = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => reject(timeoutError()), timeoutMs)
    onAbort = () => reject(signal?.reason)
    signal?.addEventListener('abort', onAbort, {once: true})
  })
  const waitFor = async <T>(work: Promise<T>): Promise<T> => {
    if (signal?.aborted) onAbort()
    const result = await Promise.race([work, interrupted])
    signal?.throwIfAborted()
    if (performance.now() >= expiresAt) throw timeoutError()
    return result
  }
  try {
    let targetId = existing?.id
    if (!existing) {
      onProgress?.({phase: 'cloning', progress: 0})
      const provisional = await waitFor(
        cloneProject(client, {
          url: cloneUrl,
          ...(cloneBranch ? {branch: cloneBranch} : {}),
          depth: 1,
        }),
      )
      targetId = provisional.id
      if (provisional.status === 'failed') {
        onProgress?.({phase: 'failed', error: provisional.error})
        throw new ProjectProvisioningError(
          'clone-failed',
          provisional.error ?? 'The repository clone failed on the host.',
        )
      }
      if (isReady(provisional)) {
        onProgress?.({phase: 'ready'})
        return toResolved(provisional)
      }
      onProgress?.({phase: 'cloning', progress: provisional.progress ?? 0})
    } else {
      onProgress?.({phase: 'cloning', progress: existing.progress ?? 0})
    }

    return await watchUntilReady({subscription, targetId, cloneUrl, onProgress, waitFor})
  } catch (error) {
    if (error instanceof ProjectProvisioningError && error.reason === 'timeout') {
      onProgress?.({phase: 'failed', error: error.message})
    }
    throw error
  } finally {
    if (timer) clearTimeout(timer)
    signal?.removeEventListener('abort', onAbort)
    void subscription.close()
  }
}

interface WatchOptions {
  subscription: Subscription
  targetId: string | undefined
  cloneUrl: string
  onProgress?: (status: ProvisioningStatus) => void
  waitFor: <T>(work: Promise<T>) => Promise<T>
}

/** Find the entry we are provisioning within a fresh catalogue snapshot. */
function selectEntry(
  projects: ProjectEntry[],
  targetId: string | undefined,
  cloneUrl: string,
): ProjectEntry | undefined {
  if (targetId) {
    const byId = projects.find(entry => entry.id === targetId)
    if (byId) return byId
  }
  return matchProjectByCloneUrl(projects, cloneUrl)
}

/**
 * Consume root-config deltas within the same absolute budget as the clone
 * request. A stream close before settling is a retryable `closed` failure.
 */
async function watchUntilReady({
  subscription,
  targetId,
  cloneUrl,
  onProgress,
  waitFor,
}: WatchOptions): Promise<ResolvedProject> {
  const iterator = subscription[Symbol.asyncIterator]()

  for (;;) {
    const result = await waitFor(iterator.next())
    if (result.done) {
      // The root subscription dropped before settling — retryable; the runtime
      // re-establishes and re-drives provisioning.
      throw new ProjectProvisioningError('closed', 'The connection closed before the repository finished provisioning.')
    }

    const evt = result.value
    if (evt.type !== 'action') continue
    const action = evt.params.action
    if (!isRootConfigChanged(action)) continue

    const projects = readProjectsFromConfigDelta(action.config)
    if (!projects) continue

    const entry = selectEntry(projects, targetId, cloneUrl)
    if (!entry) continue

    if (entry.status === 'failed') {
      onProgress?.({phase: 'failed', error: entry.error})
      throw new ProjectProvisioningError('clone-failed', entry.error ?? 'The repository clone failed on the host.')
    }
    if (isReady(entry)) {
      onProgress?.({phase: 'ready'})
      return toResolved(entry)
    }
    onProgress?.({phase: 'cloning', progress: entry.progress ?? 0})
  }
}
